import { ApolloError } from "@apollo/client";
import Levenshtein from "fast-levenshtein";
import { useLocation } from "react-router-dom";
import { useRouteParams } from "src/hooks/useRouteParams";
import { Error } from "src/routes/Error";
import { idOrAdd, projectsPath, suggestablePaths } from "src/routes/routesDef";
import { safeKeys } from "src/utils";
import { getErrorMessages } from "src/utils/error";

export function useFuzzyRouteMatch(error?: ApolloError | Error): [string[], Record<string, string>, string[]] {
  const errorMessages = error ? getErrorMessages(error) : undefined;
  const location = useLocation();
  const currentPathname = location.pathname;
  const matchedParams = useRouteParams() as Record<string, string>;

  const taggedIdsInError =
    errorMessages &&
    errorMessages.map((e) => {
      const match = e.match(/(\D*:\d*) was not found/);
      return match && match[1];
    });

  const idOrAddParam = idOrAdd.replace(":", "");
  const validParams = safeKeys(matchedParams).reduce((acc, param) => {
    return param === idOrAddParam ||
      param === "*" ||
      (taggedIdsInError && taggedIdsInError.includes(matchedParams[param]))
      ? acc
      : acc.concat(param);
  }, [] as string[]);

  const potentialPaths = suggestablePaths.filter((path) => {
    const pathParams = path.match(/\/:[^/|(]*/g)?.map((p) => p.replace("/:", ""));

    return !pathParams || pathParams.every((p) => validParams.includes(p));
  });

  const fuzzyResults: string[] = potentialPaths
    .map((p) => [Levenshtein.get(currentPathname, p.replace(/\/:[^/]*/g, "")), p] as [number, string])
    .sort(([s1], [s2]) => s1 - s2)
    .slice(0, 10)
    .map(([, path]) => path);

  return [fuzzyResults.length > 0 ? fuzzyResults : [projectsPath], matchedParams, validParams];
}
